import React, { useContext, useEffect, useRef } from "react";
import themeContext from "../../../../context/theme/ThemeContext";
import "../../styles/styles.css";

/**
 * Per-slice Key Object list popup — appears when the user clicks the ★ star in
 * the ViewportNavWidget.  Read-only — just shows the title/description of every
 * KO that references the current slice.  Styled to match the History / Clinical
 * Notes AttachmentBox dropdown (history-data-options class from styles.css).
 *
 * Props:
 *   items   – Array<{ title, description, creatorName, date }>
 *   onClose – () => void
 *   style   – optional override for the container (used for positioning)
 */
const KOListPanel = ({ items = [], onClose, style: overrideStyle }) => {
  const panelRef = useRef(null);

  // Theme-aware text colours so the panel is readable on the light/white theme
  // (previously hardcoded light greys → low contrast on a light background).
  const { getThemeColor } = useContext(themeContext);
  const primaryText = getThemeColor("primary-color");        // #FFF dark / #000 light
  const mutedText   = getThemeColor("downloads-sub-heading"); // #bfbfbf dark / #4d4d4d light

  // Close on outside click
  useEffect(() => {
    const handleOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        onClose?.();
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [onClose]);

  return (
    <div
      ref={panelRef}
      className="history-data-options"
      style={{ minWidth: 220, maxHeight: 320, overflowY: "auto", ...overrideStyle }}
    >
      <p
        className="heading-history-data-options"
        style={{ borderBottom: "1px solid #3a3a3a", paddingBottom: 7, color: primaryText }}
      >
        Key Objects
      </p>

      {items.length === 0 ? (
        <ul className="dropdown-menu-history">
          <li className="no-result" style={{ paddingLeft: 10 }}>
            <a>No Key Objects on this slice</a>
          </li>
        </ul>
      ) : (
        <ul className="dropdown-menu-history">
          {items.map((item, idx) => {
            // The KO "name" the user typed now lands in `description` (dedicated
            // key_object_description column) rather than the DICOM concept-name
            // `title`.  Show whichever is present as the single primary line so
            // the panel reads "tejas" — not a generic "Key Object N" heading with
            // the real name demoted to a subtitle.  Only render a subtitle when a
            // distinct title AND description both exist.
            const title       = item.title?.trim();
            const description = item.description?.trim();
            const primaryName = title || description || `Key Object ${idx + 1}`;
            const showSubtitle = title && description;
            return (
            <li
              key={idx}
              style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", cursor: "default" }}
            >
              {/* Filled blue star marker (visual cue this is a KO) */}
              <svg width="11" height="11" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                <path
                  d="M8 12.8589L12.944 16L11.632 10.08L16 6.09684L10.248 5.58316L8 0L5.752 5.58316L0 6.09684L4.368 10.08L3.056 16L8 12.8589Z"
                  fill="#0085FF"
                />
              </svg>

              <div style={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1 }}>
                <span
                  title={primaryName}
                  style={{
                    fontSize: 13,
                    color: primaryText,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {primaryName}
                </span>
                {showSubtitle && (
                  <span style={{ fontSize: 11, color: mutedText, marginTop: 1, whiteSpace: "normal" }}>
                    {description}
                  </span>
                )}
                {(item.creatorName || item.date) && (
                  <span style={{ fontSize: 10, color: mutedText, marginTop: 1 }}>
                    {[item.creatorName, formatDate(item.date)].filter(Boolean).join(" · ")}
                  </span>
                )}
              </div>
            </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const formatDate = (dateStr) => {
  if (!dateStr || dateStr.length < 8) return "";
  return `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}-${dateStr.slice(6, 8)}`;
};

export default KOListPanel;
